import {
  ArrowDown,
  ArrowRight,
  Waypoints,
  Circle,
  Clock3,
  Flag,
  Layers,
  MessageCircleQuestion,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Process } from "../../api/types";
import { formatDuration, parseUtcDate } from "../../utils";
import { useProcessInstanceOptions } from "./ProcessInstancePicker";
import { occurrenceActionClass } from "./ProcessOccurrenceEvidence";
import ProcessRunRow from "./ProcessRunRow";
import {
  END_NODE_ID,
  type ProcessEdge,
  START_NODE_ID,
  averageEdgeWaitSeconds,
  edgeInstanceStepIndexes,
} from "./processGraphModel";
import { HelpHint, ShowAllButton, SortHeader, StepAppIcon } from "./processSidebarUi";

type CaseSortColumn = "date" | "duration";

export default function ProcessTransitionDetail({
  from,
  to,
  edges,
  process,
  instancesInScope,
  onOpenInstanceView,
  onAsk,
}: {
  from: string;
  to: string;
  edges: ProcessEdge[];
  process: Process;
  instancesInScope: Process["instances"];
  onOpenInstanceView?: (instanceId: string, stepIndex: number) => void;
  onAsk?: (
    from: { id: string; name: string },
    to: { id: string; name: string; pct: number },
  ) => void;
}) {
  const { t } = useTranslation();
  const [showAllCases, setShowAllCases] = useState(false);
  const [sort, setSort] = useState<{ column: CaseSortColumn; descending: boolean }>({
    column: "date",
    descending: false,
  });
  const selected = edges.find(edge => edge.from === from && edge.to === to);
  const count = selected?.transitionsCount ?? 0;
  const total = edges
    .filter(edge => edge.from === from)
    .reduce((sum, edge) => sum + edge.transitionsCount, 0);
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
  const averageWait = selected ? averageEdgeWaitSeconds(selected) : null;
  const terminal = from === START_NODE_ID || to === END_NODE_ID;
  const endpoint = (id: string) => {
    const node = process.nodes.find(node => node.id === id);
    return {
      id,
      name: id === START_NODE_ID ? t("start") : id === END_NODE_ID ? t("end") : (node?.name ?? id),
      app: node?.app,
    };
  };
  const source = endpoint(from);
  const destination = endpoint(to);
  const edgeStepIndexes = useMemo(
    () => edgeInstanceStepIndexes(instancesInScope, from, to),
    [from, instancesInScope, to],
  );
  const edgeInstanceIds = useMemo(() => new Set(edgeStepIndexes.keys()), [edgeStepIndexes]);
  const options = useProcessInstanceOptions(process, instancesInScope, edgeInstanceIds);
  const sortedOptions = useMemo(
    () =>
      [...options].sort((a, b) => {
        const difference =
          sort.column === "date"
            ? parseUtcDate(a.instance.source.traceStart).getTime() -
              parseUtcDate(b.instance.source.traceStart).getTime()
            : a.instance.source.avgDuration - b.instance.source.avgDuration;
        return (sort.descending ? -1 : 1) * difference;
      }),
    [options, sort],
  );
  const onSort = (column: CaseSortColumn) =>
    setSort(current => ({
      column,
      descending: current.column === column ? !current.descending : column === "duration",
    }));

  return (
    <div className="@container mx-auto max-w-5xl space-y-4">
      <header>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1 basis-64">
            {[source, destination].map((step, index) => (
              <React.Fragment key={index === 0 ? "from" : "to"}>
                {index === 1 && (
                  <div className="flex h-6 w-10 items-center justify-center text-text-dim">
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                  </div>
                )}
                <div className="flex items-center gap-2.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-outline bg-surface">
                    {step.id === START_NODE_ID ? (
                      <Circle className="h-4 w-4 text-text-mid" aria-hidden="true" />
                    ) : step.id === END_NODE_ID ? (
                      <Flag className="h-4 w-4 text-text-mid" aria-hidden="true" />
                    ) : (
                      <StepAppIcon app={step.app} className="h-6 w-6" fallback="space" />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-text-dim">
                      {t(index === 0 ? "processPanel.transitionFrom" : "processPanel.transitionTo")}
                    </p>
                    <h3 className="break-words text-base font-semibold text-text">{step.name}</h3>
                    {step.app && <p className="mt-0.5 text-xs text-text-dim">{step.app}</p>}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
          {onAsk && (
            <button
              type="button"
              className={`${occurrenceActionClass} shrink-0`}
              onClick={() => onAsk(source, { ...destination, pct: percentage })}
            >
              <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
              {t("processPanel.transitionAsk")}
            </button>
          )}
        </div>
        <p className="mt-3 text-xs leading-relaxed text-text-dim">
          {t("processPanel.transitionScope", { count: instancesInScope.length })}
        </p>
      </header>
      <div className="overflow-hidden rounded-lg border border-outline bg-surface">
        <dl className="grid grid-cols-1 divide-y divide-outline text-xs @[300px]:grid-cols-3 @[300px]:divide-x @[300px]:divide-y-0">
          {[
            {
              label: t("edge_average_wait_label"),
              value: terminal
                ? t("processPanel.transitionNotApplicable")
                : averageWait === null
                  ? "—"
                  : formatDuration(averageWait),
              help: terminal
                ? t("processPanel.transitionTerminalTiming")
                : t("edge_average_wait_tooltip"),
              icon: Clock3,
            },
            { label: t("processPanel.transitionCount"), value: count, icon: Waypoints },
            {
              label: t("processPanel.tab_cases"),
              value: `${options.length} / ${instancesInScope.length}`,
              icon: Layers,
            },
          ].map(metric => (
            <div key={metric.label} className="flex min-w-0 flex-col px-3 py-3">
              <dt className="flex flex-1 flex-wrap content-start items-center gap-1.5 text-text-mid">
                <metric.icon
                  className="hidden h-3.5 w-3.5 shrink-0 @[400px]:block"
                  aria-hidden="true"
                />
                {metric.label}
                {metric.help && <HelpHint label={metric.label} text={metric.help} standalone />}
              </dt>
              <dd className="mt-1 text-lg font-semibold leading-6 tabular-nums text-text">
                {metric.value}
              </dd>
            </div>
          ))}
        </dl>
        <p className="border-t border-outline px-3 py-2 text-xs leading-relaxed text-text-dim">
          {terminal
            ? t("processPanel.transitionTerminalTiming")
            : averageWait === null
              ? t("processPanel.transitionNoTiming")
              : t("processPanel.transitionWaitCoverage", {
                  count: selected?.waitObservations ?? 0,
                  total: count,
                })}
        </p>
      </div>
      <section
        aria-label={t("edge_departures_label")}
        className="rounded-lg border border-info/20 bg-info/5 p-3"
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-text">
            {t("edge_departures_label")}
            <HelpHint
              label={t("edge_departures_label")}
              text={t("edge_departures_tooltip")}
              standalone
            />
          </h3>
          <span className="text-lg font-semibold tabular-nums text-info-strong">{percentage}%</span>
        </div>
        <div className="my-2 h-1.5 overflow-hidden rounded-full bg-info/10" aria-hidden="true">
          <div className="h-full rounded-full bg-info/60" style={{ width: `${percentage}%` }} />
        </div>
        <p className="text-xs leading-relaxed text-text-mid">
          {t("processPanel.transitionDepartures", { count, total })}
        </p>
      </section>
      {onOpenInstanceView && options.length > 0 && (
        <section
          className="border-t border-outline pt-4"
          aria-label={t("instances_through_transition_title")}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-text">
              {t("instances_through_transition_title")}
            </h3>
            <span className="rounded-full bg-surface-strong px-2 py-0.5 text-xs font-medium tabular-nums text-text-mid">
              {options.length}
            </span>
          </div>
          <p className="mb-3 text-xs text-text-dim">{t("processPanel.transitionCasesHint")}</p>
          <div className="overflow-hidden rounded-lg border border-outline">
            <div className="grid grid-cols-1 gap-3 border-b border-outline bg-surface-strong/50 px-3 py-2 text-xs text-text-dim @[480px]:grid-cols-[minmax(0,1fr)_108px]">
              <div className="flex items-center justify-between gap-3">
                <SortHeader
                  label={t("processPanel.columnCase")}
                  title={t("processPanel.sortBy", { column: t("scope_filter_date") })}
                  column="date"
                  sort={sort}
                  onSort={onSort}
                />
                <SortHeader
                  label={t("processPanel.transitionCaseDuration")}
                  column="duration"
                  sort={sort}
                  onSort={onSort}
                  align="right"
                />
              </div>
            </div>
            <ul className="divide-y divide-outline">
              {(showAllCases ? sortedOptions : sortedOptions.slice(0, 5)).map((option, index) => (
                <li
                  key={option.instance.id}
                  className={`grid grid-cols-1 items-center gap-3 p-3 @[480px]:grid-cols-[minmax(0,1fr)_108px] ${index % 2 ? "bg-surface-strong/40" : "bg-surface"}`}
                >
                  <ProcessRunRow run={option.instance} label={option.identity.instanceLabel} />
                  <button
                    type="button"
                    onClick={() => {
                      const stepIndex = edgeStepIndexes.get(option.instance.id);
                      if (stepIndex !== undefined)
                        onOpenInstanceView(option.instance.id, stepIndex);
                    }}
                    aria-label={t("open_instance_view_hint", { label: option.identity.fullLabel })}
                    className={`${occurrenceActionClass} justify-self-end`}
                  >
                    {t("processPanel.stepOpenCase")}
                    <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {options.length > 5 && (
            <ShowAllButton
              expanded={showAllCases}
              label={t("processPanel.stepAllCases", { count: options.length })}
              onClick={() => setShowAllCases(value => !value)}
            />
          )}
        </section>
      )}
    </div>
  );
}
